import * as React from "react";
import { cn } from "../../../lib/utils";

export interface PillOption {
  value: string;
  label: string;
  description?: string;
}

export interface StoryPillsProps {
  /** Array of pill options to display */
  options: PillOption[];
  /** Currently selected value */
  value: string | null;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Disable all pills */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Aria label for the pill group */
  ariaLabel?: string;
  /** Make pills expand to fill available width equally */
  fullWidth?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message displayed below the pills */
  errorMessage?: string;
}

/**
 * StoryPills - A pill selector component for toggling between options
 *
 * Features:
 * - Visual states: selected (blue border, blue text) / unselected (gray border)
 * - Optional description text below each label
 * - Keyboard accessible with proper ARIA attributes
 * - Uses capsule-preset design tokens
 *
 * @example
 * ```tsx
 * <StoryPills
 *   options={[
 *     { value: 'Macro', label: 'Macro', description: 'Collective Impact' },
 *     { value: 'Micro', label: 'Micro', description: 'Personal Moment' },
 *   ]}
 *   value={selectedStory}
 *   onChange={setSelectedStory}
 * />
 * ```
 */
function StoryPills({
  options,
  value,
  onChange,
  disabled = false,
  className,
  ariaLabel = "Select an option",
  fullWidth = false,
  error = false,
  errorMessage,
}: StoryPillsProps) {
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionValue: string,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled) {
        onChange(optionValue);
      }
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-invalid={error}
        className={cn("flex gap-2", className)}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => !disabled && onChange(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              className={cn(
                // Base styles - h-16 = 64px per Figma
                "relative flex h-16 flex-col items-center justify-center rounded-lg px-2 py-1",
                "transition-all duration-200",
                // Border - 1.5px per Figma
                "border-[1.5px] border-solid",
                // Width - fullWidth makes pills grow equally
                fullWidth ? "flex-1" : "min-w-[120px]",
                // Overflow hidden for the left accent
                "overflow-hidden",
                // State-based styles
                error
                  ? "border-stroke-critical bg-bg-critical"
                  : isSelected
                    ? "border-stroke-blue bg-bg-primary"
                    : "border-stroke-secondary bg-bg-primary hover:border-stroke-primary",
                // Disabled styles
                disabled && "cursor-not-allowed opacity-50",
                // Focus styles
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue focus-visible:ring-offset-2",
              )}
            >
              {/* Left accent bar when selected */}
              {isSelected && (
                <span className="absolute left-0 top-0 h-full w-1 bg-brand-blue" />
              )}
              <span
                className={cn(
                  "text-base font-bold leading-6",
                  isSelected ? "text-text-link" : "text-text-primary",
                )}
              >
                {option.label}
              </span>
              {option.description && (
                <span
                  className={cn(
                    "text-sm leading-5",
                    isSelected ? "text-text-link" : "text-text-secondary",
                  )}
                >
                  {option.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {error && errorMessage && (
        <p className="px-4 text-xs leading-4 text-text-critical">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

StoryPills.displayName = "StoryPills";

export { StoryPills };
